/**
 * ShieldBar — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.shieldBar,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { HUD_BAR_ICON_SCALE, HUD_PLAYER_NAME_FONT_SIZE } from './constants.js';
import { shieldFillRatio } from './shieldFill.js';

export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);

  // Shield icon (+ symbol) tinted blue
  const icon = new PIXI.Graphics();
  icon.setStrokeStyle({ width: 3, color: 0x33ccff });
  icon.moveTo(-8, 0);
  icon.lineTo(8, 0);
  icon.moveTo(0, -8);
  icon.lineTo(0, 8);
  icon.stroke();
  container.addChild(icon);

  const border = new PIXI.Graphics();
  container.addChild(border);

  const background = new PIXI.Graphics();
  container.addChild(background);

  // Shield fill - use Sprite instead of Graphics for efficient scaling
  const fill = new PIXI.Sprite(PIXI.Texture.WHITE);
  fill.anchor.set(0, 0);
  fill.tint = 0x33ccff; // Shield blue
  container.addChild(fill);

  const text = new PIXI.Text({
    text: '0',
    style: {
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 'bold',
      fill: 0xffffff
    }
  });
  text.anchor.set(0, 0.5);
  container.addChild(text);

  // Player name text (above shield bar)
  const nameText = new PIXI.Text({
    text: 'Player',
    style: {
      fontFamily: 'Arial',
      fontSize: HUD_PLAYER_NAME_FONT_SIZE,
      fontWeight: 'normal',
      fill: 0xffffff
    }
  });
  nameText.anchor.set(0, 1); // Anchor bottom-left
  container.addChild(nameText);

  hud.elements.shieldBar = { container, icon, border, background, fill, text, nameText };
  hud._positionShieldBar();
}

/**
 * Position shield bar
 */
export function position(hud) {
  const hb = hud.elements.healthBar;
  const sb = hud.elements.shieldBar;
  if (!hb || !sb || hb.barX === undefined) return;

  const spacing = 8;
  const shieldBarY = hb.barY - hb.barHeight - spacing;
  const barX = hb.barX;
  const barWidth = hb.barWidth;
  const barHeight = hb.barHeight;

  sb.icon.scale.set(HUD_BAR_ICON_SCALE);
  sb.icon.x = hb.icon.x;
  sb.icon.y = shieldBarY + barHeight / 2;

  sb.border.clear();
  sb.border.setStrokeStyle({ width: 2, color: 0xffffff });
  sb.border.rect(barX, shieldBarY, barWidth, barHeight);
  sb.border.stroke();

  sb.background.clear();
  sb.background.rect(barX + 2, shieldBarY + 2, barWidth - 4, barHeight - 4);
  sb.background.fill({ color: 0x111a26 });

  // Position and size the fill sprite
  sb.fill.x = barX + 2;
  sb.fill.y = shieldBarY + 2;
  sb.fill.width = barWidth - 4;
  sb.fill.height = barHeight - 4;

  sb.barX = barX;
  sb.barY = shieldBarY;
  sb.barWidth = barWidth;
  sb.barHeight = barHeight;
  sb.fillMaxWidth = barWidth - 4; // Store max width for scaling

  if (sb.text) {
    sb.text.x = barX + barWidth + 15;
    sb.text.y = shieldBarY + barHeight / 2;
  }

  // Position player name text above shield bar
  if (sb.nameText) {
    sb.nameText.x = barX;
    sb.nameText.y = shieldBarY - 5; // 5px gap above the shield bar
  }
}

export function update(hud, localPlayer) {
  const sb = hud.elements.shieldBar;
  if (!sb) return;

  const shieldValue = Math.max(0, localPlayer.shieldHealth || 0);
  const shieldPercent = shieldFillRatio(shieldValue, localPlayer.maxShield);

  // Update fill using width instead of clear/redraw (much more efficient)
  sb.fill.width = sb.fillMaxWidth * shieldPercent;

  if (sb.text) {
    sb.text.text = Math.round(shieldValue).toString();
  }
}
